import { useState } from 'react';
import useToastMessage from '@lib/logger/toast.ts';
import { useTranslation } from 'react-i18next';

declare global {
    interface Window {
        localStream?: MediaStream;
    }
} 

interface Microphone { 
    isMicrophoneAllowed: boolean; 
    validateMicrophoneStatus: () => Promise<void>;
    setupMicrophonePermissions: () => Promise<MediaStreamResponse>;
}

interface MediaStreamResponse {
    stream?: MediaStream;
    error?: { 
        message: string; 
    };
}

function useMicrophone (): Microphone {
  const { t } = useTranslation();
  const [isMicrophoneAllowed, setMicrophoneAllowed] = useState<boolean>(true);
  const { showToast } = useToastMessage();
  
  const resetPermissions = (): void => {
    window.localStream?.getTracks().forEach((track) => {
      track.stop(); 
    }); 
    window.localStream = undefined; 
    setMicrophoneAllowed(false); 
  }; 
  
  async function setupMicrophonePermissions (): Promise<MediaStreamResponse> {
    
    try {
      const stream = await navigator.mediaDevices.getUserMedia({ 
        video: false, 
        audio: true, 
      }); 

      window.localStream = stream;
      showToast('success', t('microphone.toastMessage.success', 'Microphone is ready.'));
      setMicrophoneAllowed(true);
      
      return { 
        stream 
      };

    } catch (err) {
      showToast('warning', t('microphone.toastMessage.warning', 'Microphone is not ready. Please enable microphone permissions in your browser.'));
      setMicrophoneAllowed(false);
      resetPermissions(); 
      
      return { 
        error: { 
          message: 'Could not get microphone permissions. Please check your browser settings.', 
        }, 
      }; 
    } 
  }
  
  async function validateMicrophoneStatus (): Promise<void> {
    const isMicrophoneCheckAvailable = window.location.protocol === 'https:' || window.location.hostname === 'app.tendril.local';
    const permissionStatus = await navigator.permissions.query({ name: 'microphone' as PermissionName }); 
    const permissionGranted = permissionStatus.state === 'granted'; 
    
    if (!isMicrophoneCheckAvailable) {
      showToast('info', t('microphone.toastMessage.info', 'Microphone permission check is not available.'));
      setMicrophoneAllowed(false);
    }

    if (!permissionGranted) {
      setMicrophoneAllowed(false);
      void setupMicrophonePermissions();
    } 
    setMicrophoneAllowed(true);
  }
  
  return { 
    isMicrophoneAllowed, 
    validateMicrophoneStatus, 
    setupMicrophonePermissions,
  };
}

export default useMicrophone;